import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Sidebar from "../../components/scandinavianAirlines/Sidebar";
import ResultsHeader from "../../components/scandinavianAirlines/ResultsHeader";
import SearchSummary from "../../components/scandinavianAirlines/SearchSummary";
import LoadingSpinner from "../../components/scandinavianAirlines/LoadingSpinner";
import FlightResultsSection from "../../components/scandinavianAirlines/FlightResultsSection";
import { buildMockSasMultiClassResponse } from "../../services/mockOffers";
import { groupFlightsByRoute } from "../../utils/flightMatcher";
import { decodeSearchParams } from "../../utils/searchUtils";

const FlightSearchResults: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [flightResults, setFlightResults] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedOffer, setSelectedOffer] = useState<{
        offer: any; // The complete PSS-shaped FlightOffer from the mock search
        cabinClass: "economy" | "premiumEconomy" | "business";
        price: number;
        taxes: number;
    } | null>(null);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const searchInitiatedRef = useRef(false);
    const lastSearchRef = useRef<string | null>(null);

    const searchString = searchParams.get("search");
    const searchData = searchString ? decodeSearchParams(searchString) : null;

    useEffect(() => {
        // Skip if we've already searched for this exact query
        if (lastSearchRef.current === searchString) {
            return;
        }

        // Skip first render in StrictMode
        if (
            searchInitiatedRef.current &&
            lastSearchRef.current === null &&
            searchString
        ) {
            return;
        }

        const searchFlights = async () => {
            if (!searchString || !searchData) {
                setError(searchString ? "Invalid search parameters" : null);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Mark that we've initiated a search
                searchInitiatedRef.current = true;
                lastSearchRef.current = searchString;

                // Search is synthesised client-side in the PSS flight-offer
                // shape (the Amadeus test API was retired in MOVMO-590):
                // the same flights priced in economy / premium / business.
                const toIsoDate = (d?: string) =>
                    d && /^\d{8}$/.test(d)
                        ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`
                        : d;
                const multiClassResults = buildMockSasMultiClassResponse({
                    origin: searchData.origin,
                    destination: searchData.destination,
                    departureDate: toIsoDate(searchData.departureDate)!,
                    returnDate: toIsoDate(searchData.returnDate),
                    adults: searchData.adults,
                    children: searchData.children || 0,
                    infants: searchData.infants || 0,
                });

                // Group flights by route for display
                const groupedFlights = groupFlightsByRoute(
                    multiClassResults.economy,
                    multiClassResults.premiumEconomy,
                    multiClassResults.business
                );

                setFlightResults({
                    data: groupedFlights,
                    dictionaries: multiClassResults.dictionaries,
                    meta: multiClassResults.meta,
                });
            } catch (err: any) {
                console.error("Error searching flights:", err);
                setError("Failed to search flights. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        searchFlights();
    }, [searchString, searchData]); // Include searchData in dependencies for proper re-render

    const handleNewSearch = () => {
        navigate("/");
    };

    const formatPriceWithSpace = (amount: number) => {
        const [whole, decimal] = amount.toFixed(2).split(".");
        const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
        return `${formattedWhole},${decimal}`;
    };

    const getTotalPrice = () => {
        if (!selectedOffer) return "0,00";
        return formatPriceWithSpace(selectedOffer.price);
    };

    const getTaxesAndFees = () => {
        if (!selectedOffer) return "0,00";
        return formatPriceWithSpace(selectedOffer.taxes);
    };

    if (!searchData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        Invalid search parameters
                    </h2>
                    <button
                        onClick={() => navigate("/")}
                        className="px-6 py-3 bg-sas-blue text-white rounded-full hover:bg-sas-blue-dark"
                    >
                        Go to Search
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f5f5f5] pb-24">
            {/* Sidebar - overlays content when collapsed */}
            <Sidebar
                activeItem="Book"
                isCollapsed={isCollapsed}
                onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
            />

            {/* Main Content - full width page */}
            <div className="min-h-screen">
                {/* Header with max-width content */}
                <ResultsHeader currentStep={1} />

                {/* Search Summary */}
                <SearchSummary
                    searchData={searchData}
                    onNewSearch={handleNewSearch}
                />

                {/* Main Content Area */}
                <div className="max-w-[1120px] mx-auto px-8 py-8">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : error ? (
                        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                            <p className="text-red-600 mb-4">{error}</p>
                            <button
                                onClick={handleNewSearch}
                                className="px-6 py-3 bg-sas-blue text-white rounded-full hover:bg-sas-blue-dark"
                            >
                                New Search
                            </button>
                        </div>
                    ) : (
                        <div>
                            <h2 className="text-3xl font-semibold text-[#000099] mb-6">
                                Select flights
                            </h2>

                            {/* Outbound Flights */}
                            {flightResults &&
                                flightResults.data &&
                                flightResults.data.length > 0 && (
                                    <FlightResultsSection
                                        origin={
                                            searchData.origin === "OSL"
                                                ? "Oslo"
                                                : searchData.origin
                                        }
                                        originCode={searchData.origin}
                                        destination={
                                            searchData.destination === "NYC"
                                                ? "New York, NY"
                                                : searchData.destination
                                        }
                                        destinationCode={searchData.destination}
                                        flights={flightResults.data}
                                        departureDate={searchData.departureDate}
                                        isOutbound={true}
                                        selectedOffer={selectedOffer}
                                        onSelectFlight={(
                                            flight,
                                            cabinClass,
                                            price,
                                            fullOffer
                                        ) => {
                                            const taxes =
                                                parseFloat(
                                                    fullOffer.price.total
                                                ) -
                                                parseFloat(
                                                    fullOffer.price.base
                                                );
                                            setSelectedOffer({
                                                offer: fullOffer,
                                                cabinClass,
                                                price: parseFloat(
                                                    fullOffer.price.total
                                                ),
                                                taxes,
                                            });
                                        }}
                                    />
                                )}

                            {/* Return Flights - if round trip */}
                            {searchData.tripType === "RT" &&
                                searchData.returnDate && (
                                    <FlightResultsSection
                                        origin={
                                            searchData.destination === "NYC"
                                                ? "New York, NY"
                                                : searchData.destination
                                        }
                                        originCode={searchData.destination}
                                        destination={
                                            searchData.origin === "OSL"
                                                ? "Oslo"
                                                : searchData.origin
                                        }
                                        destinationCode={searchData.origin}
                                        flights={flightResults?.data || []}
                                        departureDate={searchData.returnDate}
                                        isOutbound={false}
                                        selectedOffer={selectedOffer}
                                        onSelectFlight={(
                                            flight,
                                            cabinClass,
                                            price,
                                            fullOffer
                                        ) => {
                                            const taxes =
                                                parseFloat(
                                                    fullOffer.price.total
                                                ) -
                                                parseFloat(
                                                    fullOffer.price.base
                                                );
                                            setSelectedOffer({
                                                offer: fullOffer,
                                                cabinClass,
                                                price: parseFloat(
                                                    fullOffer.price.total
                                                ),
                                                taxes,
                                            });
                                        }}
                                    />
                                )}
                        </div>
                    )}

                    {/* Disclaimer Text */}
                    {!loading && !error && (
                        <div className="-mt-6 text-xs text-gray-600 leading-relaxed w-[1125px] mx-auto">
                            • Displayed prices applies to one adult including
                            taxes & carrier-imposed fees. • The above products,
                            rules and services apply on SAS-operated flights. •
                            Products and services offered by our partner
                            airlines may differ. • SAS Plus is not available on
                            SAS codeshare flights and connecting flights
                            operated by other carriers. • Additional baggage
                            fees may apply. • Out-and inbound fares are
                            interdependent and subject to change. • The Ticket
                            is only valid for the sequence of transportation
                            shown on it. More info in Conditions of carriage
                            section 4.5. • * If you book a flight more than 7
                            days before departure to/from the US you can cancel
                            it for free within 24 hours of the time of the first
                            reservation.
                        </div>
                    )}
                </div>

                {/* Bottom Summary Bar */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
                    <div className="max-w-[1120px] mx-auto px-8 relative">
                        {/* View trip button - centered and positioned at top */}
                        <div className="absolute left-[53%] transform -translate-x-1/2 -top-6">
                            <button className="bg-white px-4 py-1 rounded-md shadow-t-md text-sas-blue text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors">
                                View trip
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div className="py-4 flex justify-between items-center">
                            {/* Left Column - Trip Summary */}
                            <div className="flex items-start gap-12">
                                <div className="flex flex-col justify-between h-14">
                                    <div className="text-sm text-[#383838] uppercase font-bold">
                                        {searchData.tripType === "RT"
                                            ? "Round Trip"
                                            : "One Way"}
                                    </div>
                                    <div className="text-sm text-[#383838] leading-tight">
                                        {searchData.origin} –{" "}
                                        {searchData.destination}
                                        {searchData.tripType === "RT" && (
                                            <>
                                                <br />
                                                {searchData.destination} –{" "}
                                                {searchData.origin}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-col justify-between h-14">
                                    <div className="text-sm text-[#383838] uppercase font-bold">
                                        Dates
                                    </div>
                                    <div className="text-sm text-[#383838]">
                                        {(() => {
                                            const formatDate = (
                                                dateStr: string
                                            ) => {
                                                const date = new Date(dateStr);
                                                const day = date.getDate();
                                                const month = date
                                                    .toLocaleDateString(
                                                        "en-US",
                                                        { month: "short" }
                                                    )
                                                    .toUpperCase();
                                                return `${day} ${month}`;
                                            };

                                            if (searchData.tripType === "RT") {
                                                return `${formatDate(
                                                    searchData.departureDate
                                                )} – ${formatDate(
                                                    searchData.returnDate ||
                                                        searchData.departureDate
                                                )}`;
                                            }
                                            return formatDate(
                                                searchData.departureDate
                                            );
                                        })()}
                                    </div>
                                </div>

                                <div className="flex flex-col justify-between h-14">
                                    <div className="text-sm text-[#383838] uppercase font-bold">
                                        Travelers
                                    </div>
                                    <div className="text-sm text-[#383838]">
                                        {searchData.adults +
                                            searchData.children +
                                            searchData.infants}{" "}
                                        {searchData.adults +
                                            searchData.children +
                                            searchData.infants ===
                                        1
                                            ? "adult"
                                            : "adults"}
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Price and Actions */}
                            <div className="flex items-center gap-6 -mr-16">
                                <div className="flex items-start gap-3">
                                    {/* Shopping Cart Icon */}
                                    <svg
                                        className="w-5 h-5 text-[#3f3d3a]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                        />
                                    </svg>
                                    <div>
                                        <div className="text-sm text-[#383838] uppercase font-bold">
                                            Total
                                        </div>
                                        <div className="text-md font-bold text-[#383838]">
                                            {getTotalPrice()} EUR
                                        </div>
                                    </div>
                                </div>

                                <div className="text-xs">
                                    <a
                                        href="#"
                                        className="text-sas-blue underline"
                                    >
                                        incl. {getTaxesAndFees()} EUR
                                        <br />
                                        Taxes & carrier-imposed fees
                                    </a>
                                </div>

                                <button
                                    className="px-8 py-2.5 bg-[#0103bd] text-white rounded-full text-sm font-bold hover:bg-[#0102a0] uppercase tracking-wider transition-colors"
                                    disabled={!selectedOffer}
                                    onClick={() => {
                                        if (selectedOffer) {
                                            // Store flight data in sessionStorage similar to Philippine Airlines
                                            const storedData = {
                                                flight: selectedOffer.offer,
                                                cabinClass:
                                                    selectedOffer.cabinClass,
                                                price: selectedOffer.price,
                                                taxes: selectedOffer.taxes,
                                                tripType: searchData.tripType,
                                                origin: searchData.origin,
                                                destination:
                                                    searchData.destination,
                                                departureDate:
                                                    searchData.departureDate,
                                                returnDate:
                                                    searchData.returnDate,
                                                passengers: {
                                                    adults: searchData.adults,
                                                    children:
                                                        searchData.children,
                                                    infants: searchData.infants,
                                                },
                                                dictionaries:
                                                    flightResults?.dictionaries ||
                                                    {},
                                            };
                                            sessionStorage.setItem(
                                                "selectedSASFlightOffer",
                                                JSON.stringify(storedData)
                                            );
                                            navigate(
                                                "/scandinavian-airlines/travelers"
                                            );
                                        }
                                    }}
                                >
                                    {!selectedOffer
                                        ? "SELECT OUTBOUND"
                                        : "CONTINUE"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlightSearchResults;
